import { FC, ReactElement } from 'react';
import { View } from '../../types';
import { TruckIcon, DashboardIcon, DocumentTextIcon, BookOpenIcon, ArchiveBoxIcon, ChartBarIcon, CogIcon, QuestionMarkCircleIcon, ArrowUpIcon, ArrowDownIcon, InformationCircleIcon, CodeBracketIcon, BeakerIcon } from '../Icons';
import { useAuth } from '../../services/auth';
import { OWNER_UID } from '../../constants';

interface SidebarProps {
    isSidebarOpen: boolean;
    currentView: View;
    logoSrc: string;
    isHelpOpen: boolean;
    setIsHelpOpen: (v: boolean) => void;
    onNavigate: (view: View) => void;
}

export const Sidebar: FC<SidebarProps> = ({
    isSidebarOpen,
    currentView,
    logoSrc,
    isHelpOpen,
    setIsHelpOpen,
    onNavigate,
}) => {
    const { currentUser, can, hasRole } = useAuth();

    const NavItem = ({ view, icon, label }: { view: View; icon: ReactElement; label: string }) => (
        <li
            onClick={() => onNavigate(view)}
            className={`flex items-center p-3 my-1 rounded-lg cursor-pointer transition-colors ${currentView === view
                ? 'bg-blue-600 text-white shadow-lg'
                : 'text-gray-600 dark:text-gray-300 hover:bg-blue-100 dark:hover:bg-gray-700'
                }`}
        >
            <span className="w-6 h-6">{icon}</span>
            <span className="ml-4 font-medium">{label}</span>
        </li>
    );

    const HelpMenu = () => (
        <li>
            <div
                onClick={() => setIsHelpOpen(!isHelpOpen)}
                className="flex items-center justify-between p-3 my-1 rounded-lg cursor-pointer text-gray-600 dark:text-gray-300 hover:bg-blue-100 dark:hover:bg-gray-700"
            >
                <div className="flex items-center">
                    <span className="w-6 h-6"><QuestionMarkCircleIcon /></span>
                    <span className="ml-4 font-medium">Справка</span>
                </div>
                {isHelpOpen ? <ArrowUpIcon className="w-5 h-5" /> : <ArrowDownIcon className="w-5 h-5" />}
            </div>
            {isHelpOpen && (
                <ul className="pl-8 transition-all duration-300 ease-in-out">
                    <NavItem view="USER_GUIDE" icon={<BookOpenIcon />} label="Руководство" />
                    <NavItem view="ADMIN_GUIDE" icon={<CogIcon />} label="Администратору" />
                    {hasRole('admin') && currentUser?.id === OWNER_UID && (
                        <>
                            <NavItem view="DEVELOPER_GUIDE" icon={<CodeBracketIcon />} label="Разработчику" />
                            <NavItem view="TESTING_GUIDE" icon={<BeakerIcon />} label="Тестировщику" />
                        </>
                    )}
                    <NavItem view="ABOUT" icon={<InformationCircleIcon />} label="О программе" />
                </ul>
            )}
        </li>
    );

    return (
        <aside
            className={`bg-white dark:bg-gray-800 text-gray-800 dark:text-white flex flex-col transition-all duration-300 ease-in-out shadow-xl z-20 ${isSidebarOpen ? 'w-64' : 'w-0'
                } overflow-hidden`}
        >
            <div className="flex items-center justify-center h-20 border-b dark:border-gray-700 p-4">
                <img
                    src={logoSrc}
                    alt="Logo"
                    className="h-12 w-auto object-contain"
                    onError={(e) => {
                        e.currentTarget.style.display = 'none';
                        e.currentTarget.nextElementSibling?.classList.remove('hidden');
                    }}
                />
                <TruckIcon className="hidden h-8 w-8 text-blue-500" />
                <h1 className="text-2xl font-bold ml-2 text-gray-800 dark:text-white truncate">Путевые листы</h1>
            </div>
            <nav className="flex-1 px-4 py-4">
                <ul>
                    <NavItem view="DASHBOARD" icon={<DashboardIcon />} label="Панель управления" />
                    <NavItem view="WAYBILLS" icon={<DocumentTextIcon />} label="Путевые листы" />
                    <NavItem view="DICTIONARIES" icon={<BookOpenIcon />} label="Справочники" />
                    <NavItem view="WAREHOUSE" icon={<ArchiveBoxIcon />} label="Склад" />
                    <NavItem view="REPORTS" icon={<ChartBarIcon />} label="Отчеты" />
                    {can(['admin.panel', 'import.run', 'import.limited', 'export.run', 'audit.business.read'], 'or') && (
                        <NavItem view="ADMIN" icon={<CogIcon />} label="Настройки" />
                    )}
                    <HelpMenu />
                    <div className="h-px bg-gray-200 dark:bg-gray-700 my-2"></div>
                </ul>
            </nav>
            <div className="p-4 border-t dark:border-gray-700">
                {/* <DevRoleSwitcher /> */}
            </div>
        </aside>
    );
};
